import type { ReactNode } from "react";
import { Building2 } from "lucide-react";

import { Badge } from "@/components/ui/badge";

import { agencyInternalState } from "../agencies/internalAgencies";
import type { EchoflicksVideoSession } from "../types";
import {
  StudioDirectoryContext,
  useStudioDirectoryContext,
} from "./studioDirectoryContext";
import {
  isStudioFilm,
  studioAgencyLabel,
  studioCreator,
  studioCreatorLabel,
  studioFilmDetail,
} from "./studioFilms";
import { useStudioDirectory } from "./useStudioDirectory";

/**
 * List cells for Studio films (B6.3). A Studio film has no WhatsApp user, only
 * a phantom `studio-…` id, so lists show its agency and creator instead. The
 * names come from the admin-only directory, loaded once per list and shared
 * through StudioDirectoryContext; without it (non-admins) the cells fall back
 * to "Studio film" / "Portal user …".
 */
export const StudioDirectoryProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const directory = useStudioDirectory();
  return (
    <StudioDirectoryContext.Provider value={directory}>
      {children}
    </StudioDirectoryContext.Provider>
  );
};

/** The User column of a Studio film: its agency, never the phantom contact. */
export const StudioFilmUserCell = ({
  record,
}: {
  record: EchoflicksVideoSession;
}) => {
  const directory = useStudioDirectoryContext();
  const detail = studioFilmDetail(record);
  return (
    <div className="min-w-0">
      <div className="flex items-center gap-1 truncate font-medium">
        <Building2 className="size-3.5 shrink-0" aria-hidden="true" />
        {studioAgencyLabel(record, directory?.agenciesById)}
      </div>
      <div className="text-xs text-muted-foreground">
        Studio film{detail ? ` · ${detail}` : ""}
      </div>
    </div>
  );
};

export const StudioAgencyCell = ({
  record,
}: {
  record: EchoflicksVideoSession;
}) => {
  const directory = useStudioDirectoryContext();
  if (!isStudioFilm(record)) {
    return <span className="text-muted-foreground">—</span>;
  }
  const agency = record.studio_agency_id
    ? directory?.agenciesById.get(record.studio_agency_id)
    : undefined;
  const state = agency ? agencyInternalState(agency) : "customer";
  return (
    <div className="flex flex-wrap items-center gap-1">
      <span className="truncate">
        {studioAgencyLabel(record, directory?.agenciesById)}
      </span>
      {state === "prospect" ? (
        <Badge variant="outline" className="text-xs">
          Prospect
        </Badge>
      ) : state === "internal" ? (
        <Badge variant="outline" className="text-xs">
          Internal
        </Badge>
      ) : null}
    </div>
  );
};

export const StudioCreatorCell = ({
  record,
}: {
  record: EchoflicksVideoSession;
}) => {
  const directory = useStudioDirectoryContext();
  if (!isStudioFilm(record)) {
    return <span className="text-muted-foreground">—</span>;
  }
  const creator = studioCreator(record, directory?.loginsById);
  return (
    <div className="flex flex-wrap items-center gap-1">
      <span className="truncate" title={creator.id ?? undefined}>
        {studioCreatorLabel(creator)}
      </span>
      {creator.isStaff ? (
        <Badge variant="secondary" className="text-xs">
          Staff
        </Badge>
      ) : null}
    </div>
  );
};

/** Compact agency line for board cards. */
export const StudioFilmAgencyLine = ({
  record,
}: {
  record: EchoflicksVideoSession;
}) => {
  const directory = useStudioDirectoryContext();
  const detail = studioFilmDetail(record);
  return (
    <span className="inline-flex items-center gap-1">
      <Building2 className="size-3" aria-hidden="true" />
      {studioAgencyLabel(record, directory?.agenciesById)}
      {detail ? ` · ${detail}` : ""}
    </span>
  );
};
